import { configureStore, type Reducer, type UnknownAction } from '@reduxjs/toolkit'
import { enableMapSet } from 'immer'

import { auth0Deauthenticate } from '~/app/auth'
import { rootReducer, RootState } from '~/app/store/reducers'

export type { RootState }

// Enable Immer's MapSet plugin.
// This must be called on the same Immer that is used by @reduxjs/toolkit.
// Tests in redux.test.js should ensure this.
// Note that this is not recommended: see https://github.com/reduxjs/redux-toolkit/issues/466
// However, we currently store many unserializable values in the store aside from Maps and Sets, so setting
// `serializableCheck: true` at this point in time is unrealistic.
enableMapSet()

// When logging out, reset redux to its initial state
const reducer: Reducer<RootState, UnknownAction, Partial<RootState>> = (state, action) => {
  if (action.type === auth0Deauthenticate.type) {
    state = undefined
  }
  // `combineReducers` infers no preloadable state for the plain-function reducers — only the
  // `createSlice` ones declare theirs — but each defaults its own slice, so the partial preload
  // the test store passes is safe.
  return rootReducer(state as RootState | undefined, action)
}

export const createStore = (preloadedState?: Partial<RootState>, testReducer = reducer) =>
  configureStore({
    preloadedState,
    devTools: process.env.NODE_ENV === 'development' ? { serialize: true } : false,
    reducer: testReducer,
    // A callback rather than a bare call: RTK 2 removed the standalone
    // `getDefaultMiddleware` export in favour of the one handed to this option,
    // which is already bound to the store's state and dispatch types.
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        immutableCheck: {
          ignoredPaths: [
            'chat.pubnub', // Client stores some additional telemetry data.
          ],
        },
        serializableCheck: false,
      }),
  })

type AppStore = ReturnType<typeof createStore>
/**
 * The store's own dispatch, which accepts thunks as well as plain actions.
 * `useDispatch` on its own is typed to plain actions only in react-redux 9, so
 * anything dispatching a thunk needs `useAppDispatch` from `~/app/store/hooks`.
 */
export type AppDispatch = AppStore['dispatch']
